//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

export enum ScheduledItemFailureStatus {
  // Instagram-specific
  InstagramPublishingDailyLimitException = 'daily_publishing_limit',
  InstagramPublishingExpiredToken = 'expired_token',
  InstagramPublishingFailure = 'failure',
  InstagramPublishingFeatureBlockException = 'feature_block',
  InstagramPublishingImageSizeTooLarge = 'image_size_too_large',
  InstagramPublishingInvalidTaggedUser = 'invalid_tagged_user',
  InstagramPublishingMediaDownloadTimeoutException = 'media_download_timeout',
  InstagramPublishingMediaNotReadyException = 'media_not_ready',
  InstagramPublishingOAuthException = 'oauth_exception',
  InstagramPublishingPermissionDeniedException = 'permission_denied',
  InstagramPublishingThrottlingException = 'throttled',
  InstagramPublishingUnsupportedAspectRatio = 'unsupported_aspect_ratio',
  InstagramPublishingUploadException = 'upload_failure',
  InstagramPublishingVideoFailureException = 'intermittent_video_failure',
  InstagramPublishingUnexpectedRetriableException = 'unexpected_retriable',

  // Generic failures
  MaxAttemptsReached = 'max_attempts_reached',
  MissingDevice = 'missing_device',
  MissingDistribution = 'missing_distribution',
  MissingPushToken = 'missing_push_token',
  PushServiceFailure = 'push_service_failure',

  UnclassifiedError = 'unclassified',
}

export const deserializeScheduledItemFailureStatus = (
  itemFailureStatus: string,
): ScheduledItemFailureStatus => {
  if (
    Object.values(ScheduledItemFailureStatus).includes(
      itemFailureStatus as ScheduledItemFailureStatus,
    )
  ) {
    return itemFailureStatus as ScheduledItemFailureStatus;
  }

  if (
    !Object.values(LegacyScheduledItemFailureStatus).includes(
      itemFailureStatus as LegacyScheduledItemFailureStatus,
    )
  ) {
    return ScheduledItemFailureStatus.UnclassifiedError;
  }

  switch (itemFailureStatus as LegacyScheduledItemFailureStatus) {
    case LegacyScheduledItemFailureStatus.InstagramPublishingDailyLimitException:
      return ScheduledItemFailureStatus.InstagramPublishingDailyLimitException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingExpiredToken:
      return ScheduledItemFailureStatus.InstagramPublishingExpiredToken;
    case LegacyScheduledItemFailureStatus.InstagramPublishingFailure:
      return ScheduledItemFailureStatus.InstagramPublishingFailure;
    case LegacyScheduledItemFailureStatus.InstagramPublishingFeatureBlockException:
      return ScheduledItemFailureStatus.InstagramPublishingFeatureBlockException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingImageSizeTooLarge:
      return ScheduledItemFailureStatus.InstagramPublishingImageSizeTooLarge;
    case LegacyScheduledItemFailureStatus.InstagramPublishingVideoFailureException:
      return ScheduledItemFailureStatus.InstagramPublishingVideoFailureException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingInvalidTaggedUser:
      return ScheduledItemFailureStatus.InstagramPublishingInvalidTaggedUser;
    case LegacyScheduledItemFailureStatus.InstagramPublishingMediaDownloadTimeoutException:
      return ScheduledItemFailureStatus.InstagramPublishingMediaDownloadTimeoutException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingMediaNotReadyException:
      return ScheduledItemFailureStatus.InstagramPublishingMediaNotReadyException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingOAuthException:
      return ScheduledItemFailureStatus.InstagramPublishingOAuthException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingPermissionDeniedException:
      return ScheduledItemFailureStatus.InstagramPublishingPermissionDeniedException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingThrottlingException:
      return ScheduledItemFailureStatus.InstagramPublishingThrottlingException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingUnsupportedAspectRatio:
      return ScheduledItemFailureStatus.InstagramPublishingUnsupportedAspectRatio;
    case LegacyScheduledItemFailureStatus.InstagramPublishingUploadException:
      return ScheduledItemFailureStatus.InstagramPublishingUploadException;
    case LegacyScheduledItemFailureStatus.InstagramPublishingUnexpectedRetriableException:
      return ScheduledItemFailureStatus.InstagramPublishingUnexpectedRetriableException;

    case LegacyScheduledItemFailureStatus.MaxAttemptsReached:
      return ScheduledItemFailureStatus.MaxAttemptsReached;
    case LegacyScheduledItemFailureStatus.MissingDevice:
      return ScheduledItemFailureStatus.MissingDevice;
    case LegacyScheduledItemFailureStatus.MissingDistribution:
      return ScheduledItemFailureStatus.MissingDistribution;
    case LegacyScheduledItemFailureStatus.MissingPushToken:
      return ScheduledItemFailureStatus.MissingPushToken;
    case LegacyScheduledItemFailureStatus.PushServiceFailure:
      return ScheduledItemFailureStatus.PushServiceFailure;

    case LegacyScheduledItemFailureStatus.SomethingTerrible:
    case LegacyScheduledItemFailureStatus.UnclassifiedError:
    default:
      return ScheduledItemFailureStatus.UnclassifiedError;
  }
};

/**
 * @deprecated These values are all deprecated in favor of ScheduledItemFailureStatus
 */
export enum LegacyScheduledItemFailureStatus {
  InstagramPublishingDailyLimitException = 'instagram_publishing_daily_publishing_limit',
  InstagramPublishingExpiredToken = 'instagram_publishing_expired_token',
  InstagramPublishingFailure = 'instagram_publishing_failure',
  InstagramPublishingFeatureBlockException = 'instagram_publishing_feature_block_exception',
  InstagramPublishingImageSizeTooLarge = 'instagram_publishing_image_size_too_large',
  InstagramPublishingInvalidTaggedUser = 'instagram_publishing_invalid_tagged_user',
  InstagramPublishingMediaDownloadTimeoutException = 'instagram_publish_media_download_timeout_exception',
  InstagramPublishingMediaNotReadyException = 'instagram_publish_media_not_ready_exception',
  InstagramPublishingOAuthException = 'instagram_publishing_oauth_exception',
  InstagramPublishingPermissionDeniedException = 'instagram_publishing_permission_denied_exception',
  InstagramPublishingThrottlingException = 'instagram_publishing_throttling_exception',
  InstagramPublishingUnsupportedAspectRatio = 'instagram_publishing_unsupported_aspect_ratio',
  InstagramPublishingUploadException = 'instagram_publishing_upload_exception',
  InstagramPublishingVideoFailureException = 'instagram_intermittent_video_failure_exception',
  InstagramPublishingUnexpectedRetriableException = 'instagram_publishing_unexpected_retriable',

  MaxAttemptsReached = 'max_attempts',
  MissingDevice = 'device',
  MissingDistribution = 'distribution',
  MissingPushToken = 'push_token',
  PushServiceFailure = 'push_notification',

  SomethingTerrible = 'something_terrible',
  UnclassifiedError = 'unclassified_error',
}
